import React, { useEffect, useState } from "react";
import logo from "../assets/image/eblackdog_logo.png";

const API_BASE_URL = "https://patrol-react.eblackdog.net//api";

export default function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState({
    visible: false,
    message: "Ready to login",
    type: "info",
  });
  const [loading, setLoading] = useState(false);

  const showStatus = (message, type = "info") => {
    setStatus({
      visible: true,
      message,
      type,
    });
  };

  useEffect(() => {
    const remembered = localStorage.getItem("rememberedUsername");

    if (remembered) {
      setFormData((prev) => ({
        ...prev,
        username: remembered,
        remember: true,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const clearOldGuardLoginData = () => {
    localStorage.removeItem("guardToken");
    localStorage.removeItem("guardData");
    localStorage.removeItem("guardLoginType");
    localStorage.removeItem("isGuardLoggedIn");
    localStorage.removeItem("memberId");
    localStorage.removeItem("guardName");
    localStorage.removeItem("guardEmail");
    localStorage.removeItem("designation");
  };

  const startGpsOnLogin = async (token = "") => {
    if (!navigator.geolocation) return;

    const position = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 0,
        }
      );
    });

    if (!position || !position.coords) return;

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const accuracy = position.coords.accuracy || 0;

    if (
      Number.isNaN(latitude) ||
      Number.isNaN(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/gps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          action: "ensure",
          latitude,
          longitude,
          accuracy,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (data?.success && data?.tracking_id) {
        sessionStorage.setItem("gps_tracking_id", String(data.tracking_id));
      }
    } catch (error) {
      console.error("[AdminLogin] GPS start error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username.trim() || !formData.password.trim()) {
      showStatus("Please enter both username and password", "error");
      return;
    }

    setLoading(true);
    showStatus("Authenticating your credentials...", "info");

    try {
      const res = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          username: formData.username.trim(),
          password: formData.password,
        }),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok || !result.success) {
        showStatus(result.message || "Login failed", "error");
        setLoading(false);
        return;
      }

      const token = result.token;
      const rawUser = result.data || result.user || {};

      if (!token) {
        showStatus("Login success but token missing from backend", "error");
        setLoading(false);
        return;
      }

      const adminId =
        rawUser.adminId ||
        rawUser.admin_id ||
        rawUser.id ||
        rawUser.SINo ||
        rawUser.sinno ||
        null;

      const userInfo = {
        ...rawUser,
        id: adminId,
        adminId,
        role: rawUser.role || "admin",
        loginType: "admin",
      };

      if (formData.remember) {
        localStorage.setItem("rememberedUsername", formData.username.trim());
      } else {
        localStorage.removeItem("rememberedUsername");
      }

      clearOldGuardLoginData();

      localStorage.setItem("token", token);
      localStorage.setItem("authToken", token);

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("loginType", "admin");
      localStorage.setItem("role", userInfo.role);
      localStorage.setItem("userData", JSON.stringify(userInfo));

      localStorage.setItem("adminId", adminId || "");
      localStorage.setItem("company_name", userInfo.company_name || "");
      localStorage.setItem("licensedFor", userInfo.licensedFor || "");
      localStorage.setItem("admin_active", String(userInfo.admin_active ?? 0));
      localStorage.setItem("company_address", userInfo.company_address || "");
      localStorage.setItem("company_phone", userInfo.company_phone || "");
      localStorage.setItem("admin_email", userInfo.admin_email || "");
      localStorage.setItem("company_logo", userInfo.company_logo || "");
      localStorage.setItem("user_map", userInfo.user_map || "");

      showStatus("Login successful! Getting your location...", "success");

      try {
        await Promise.race([
          startGpsOnLogin(token),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch (err) {
        console.error("[AdminLogin] GPS race error:", err);
      }

      window.location.href = "/dashboard";
    } catch (error) {
      console.error("[AdminLogin] Login error:", error);
      showStatus("Network error. Please try again.", "error");
      setLoading(false);
    }
  };

  const getStatusStyles = () => {
    switch (status.type) {
      case "success":
        return "border border-emerald-200 bg-emerald-50 text-emerald-700";
      case "error":
        return "border border-red-200 bg-red-50 text-red-700";
      default:
        return "border border-blue-200 bg-blue-50 text-blue-700";
    }
  };

  const getStatusIcon = () => {
    if (status.type === "success") return "✓";
    if (status.type === "error") return "⚠";
    return "ℹ";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-800 to-slate-700 flex items-center justify-center p-6">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="bg-gradient-to-br from-[#071426] to-[#12355B] px-8 py-10 text-center text-white">
          <div className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full bg-white p-2 shadow-lg">
            <img
              src={logo}
              alt="Security Patrol Logo"
              className="h-full w-full rounded-full object-cover"
            />
          </div>

          <h1 className="text-3xl font-bold">Security Patrol</h1>

          <p className="mt-2 text-sm text-slate-200">
            Welcome back! Please login to your account
          </p>
        </div>

        <div className="px-8 py-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="username"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Username
              </label>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  👤
                </span>

                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Enter your username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-800 outline-none transition focus:border-[#12355B] focus:bg-white focus:ring-4 focus:ring-blue-100"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Password
              </label>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  🔒
                </span>

                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-slate-50 py-3 pl-10 pr-12 text-sm text-slate-800 outline-none transition focus:border-[#12355B] focus:bg-white focus:ring-4 focus:ring-blue-100"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-500"
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-[#12355B] focus:ring-[#12355B]"
                />
                Remember me
              </label>

              <a
                href="#"
                className="text-sm font-semibold text-[#12355B] hover:underline"
              >
                Forgot Password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#071426] to-[#12355B] px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-80"
            >
              {loading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
              )}

              {loading ? "Logging in..." : "Login to Account"}
            </button>
          </form>

          {status.visible && (
            <div
              className={`mt-5 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium ${getStatusStyles()}`}
            >
              <span>{getStatusIcon()}</span>
              <span>{status.message}</span>
            </div>
          )}

          <div className="mt-6 border-t border-slate-200 pt-5 text-center">
            <div className="space-y-2 text-sm text-slate-600">
              <a
                href="mailto:eblackdogsupport@nissiinfotech.in"
                className="block hover:text-[#12355B]"
              >
                eblackdogsupport@nissiinfotech.in
              </a>

              <a
                href="https://www.eblackdog.com"
                target="_blank"
                rel="noreferrer"
                className="block hover:text-[#12355B]"
              >
                www.eblackdog.com
              </a>

              <p className="font-semibold text-slate-700">
                90037-44644 / 98422-44488
              </p>
            </div>

            <p className="mt-3 text-xs text-slate-500">
              Owned by EBlack Dog Patrol Management System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}